import { useState, useEffect, useRef } from 'react';
import { Icon } from '@customerio/pluma-components/react';
import * as styles from './index.css';
import { toggle, useSidebarActive } from '@/features/sidebar';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { generate } from '@customerio/hydra-copilot';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { useDebounceCallback } from '@/hooks/use-debounce-callback';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { sentryCaptureException } from '@/utils/sentry';
import { useStore } from '@nanostores/react';

/**
 * When ever the url changes, we will show a toast with suggestions
 * for the user to try out.
 * This is useful for new users to discover features.
 */
export function ToastSuggestions({ session }: { session: CopilotSession }) {
  const [isVisible, setIsVisible] = useState(false);
  const { suggestions, regenerate } = useToastSuggestions();
  const hidingTimerRef = useRef<number | undefined>();
  const lastSuggestionPathnameRef = useRef<string | undefined>(undefined);
  const isCopilotHidden = useSidebarActive('copilot') === false;
  const toolCount = Object.keys(useStore(session.__tools)).length;

  function hideSuggestions() {
    clearTimeout(hidingTimerRef.current);
    setIsVisible(false);
  }

  function showSuggestions() {
    clearTimeout(hidingTimerRef.current);
    hidingTimerRef.current = window.setTimeout(() => {
      hideSuggestions();
    }, 30_000);
    setIsVisible(true);
  }

  const refreshSuggestions = useDebounceCallback(() => {
    lastSuggestionPathnameRef.current = location.pathname;
    regenerate().then(() => showSuggestions());
  }, 1_000);

  // regenerate suggestions when the URL changes
  const location = useLocation();
  useEffect(() => {
    hideSuggestions();
    // If the URL hasn't changed, we don't need to regenerate suggestions
    if (location.pathname === lastSuggestionPathnameRef.current) {
      return;
    }

    // If there are no tools, we don't show suggestions
    if (toolCount === 0) {
      return;
    }

    refreshSuggestions();

    return () => {
      clearTimeout(hidingTimerRef.current);
    };
  }, [location.pathname, toolCount]);

  const handleSuggestionClick = (message: string) => {
    hideSuggestions();
    toggle('copilot');
    session.sendMessage(message);
  };

  return (
    <div className={styles.toastContainer}>
      <AnimatePresence mode="sync">
        {isVisible && isCopilotHidden
          ? suggestions.map((suggestion, index) => (
              <motion.button
                key={suggestion.label}
                className={styles.toastItem}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    type: 'spring',
                    damping: 20,
                    stiffness: 300,
                    delay: (suggestions.length - 1 - index) * 0.06,
                  },
                }}
                exit={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95,
                  transition: {
                    duration: 0.2,
                    delay: index * 0.03,
                  },
                }}
                onClick={() => handleSuggestionClick(suggestion.message)}
              >
                <Icon name="sparkle" className={styles.sparkleIcon} />
                {suggestion.label}
              </motion.button>
            ))
          : null}
      </AnimatePresence>
    </div>
  );
}

function useToastSuggestions() {
  const [suggestions, setSuggestions] = useState<{ label: string; message: string }[]>([]);
  const regenerate = async () => {
    try {
      const result = await generate(
        'Suggest what the user should say to a AI powered copilot to do their job on this page. Provide clear, highly relevant suggestions. Do not literally suggest tool calls. Instead, suggest what the user should say to the copilot to achieve their goal, that will result in tool calls. If you have no suggestions, return an empty array.',
        zodToJsonSchema(
          z.array(
            z.object({
              label: z.string().describe('The label of the suggestion. This is shown as a button and should be short.'),
              message: z
                .string()
                .describe(
                  'The message to send when the suggestion is clicked. This should be a clear, complete sentence and will be sent as an instruction to the AI.',
                ),
            }),
          ),
        ),
        {
          instructions: [
            {
              instruction:
                'Produce up to 5 suggestions for the user to try out. Only return suggestions based on tools that you can call.',
            },
          ],
        },
      );
      setSuggestions(result);
    } catch (error) {
      sentryCaptureException(error);
      setSuggestions([]);
    }
  };

  return {
    suggestions,
    regenerate,
  };
}
